/**
 * Custom JSS className generators to prevent collisions between MUI4 and MUI5
 * when classnames are minimized in production builds.
 *
 * MUI4 and MUI5 both use JSS and generate classnames like "jss1", "jss2", etc.
 * When minimized, these can collide and cause styling issues. By using different
 * short prefixes, we ensure unique classnames that remain short.
 *
 * These functions follow the JSS generateClassName signature and maintain
 * separate counters for each version to ensure uniqueness.
 *
 * The generateClassName function receives:
 * - rule: The JSS rule object
 * - sheet: The JSS stylesheet object (optional)
 *
 * It should return a unique className string.
 */

// Separate counters for MUI5 to prevent collisions with other versions of MUI
let mui5Counter = 0

/**
 * Generate className for MUI5 JSS styles
 * Uses short prefix "m5" (without dash) to keep classnames minimal
 * Uses a counter-based approach similar to JSS default behavior
 */
export const generateMui5ClassName = (rule: any, sheet?: any): string => {
  mui5Counter += 1
  // Use very short prefix "m5-" to keep classnames minimal
  // Format: m5-1, m5-2, m5-3, etc.
  const prefix = 'm5-'

  // If sheet has a name/id, include it for better debugging (only in dev)
  if (process.env.NODE_ENV !== 'production' && sheet && sheet.options.name) {
    const sanitizedName = sheet.options.name.replace(/[^a-zA-Z0-9]/g, '-')
    return `${prefix}-${sanitizedName}-${mui5Counter}`
  }

  // Default: use counter with short prefix (m51, m52, m53, etc.)
  return `${prefix}${mui5Counter}`
}
